import React, { useState, useEffect } from 'react';
import { 
  ArrowRight, Package, DollarSign, Printer, ChevronLeft, ChevronRight, QrCode, 
  Boxes, Receipt, Users, Smartphone, MessageSquare, BarChart, Clock, Share2,
  CheckCircle2, HelpCircle, Building2, Moon, Sun, Search, Star, XCircle
} from 'lucide-react';
import HeroImage from '../../assets/images/_85fb3a5a-06d9-45c4-ac0a-66e42a7f1146.jpeg';

export function LandingV2({ onShowCatalog }) {
  const [activePhone, setActivePhone] = useState(0);
  const [isDark, setIsDark] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('theme');
      return saved === 'dark' || (saved === null && window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    return false;
  });

  const [activeFeature, setActiveFeature] = useState(0);

  // Autoplay effect for features carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % 5);
    }, 3000);

    return () => clearInterval(timer);
  }, []);

  // Efecto para aplicar el tema y guardarlo
  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  // Efecto para escuchar cambios en el sistema
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      setIsDark(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const phones = [
    {
      title: "Gestiona tu inventario",
      description: "Mantén un control preciso de tus productos, precios y stock. Organiza por categorías, actualiza precios masivamente y comparte tu catálogo digital.",
      content: (
        <div className="h-full flex flex-col">
          <div className="bg-[#3B7C5B] text-white py-2 px-3 flex items-center justify-between text-xs">
            <span>13:45 | Tibó</span>
          </div>
          <div className="flex-1 p-3">
            <div className="grid grid-cols-2 gap-2">
              {[
                { name: 'Tomates', price: '$950', unit: 'cajón', stock: 15, category: 'Verduras' },
                { name: 'Papas', price: '$750', unit: 'bolsa', stock: 8, category: 'Verduras' },
                { name: 'Manzanas', price: '$1200', unit: 'cajón', stock: 20, category: 'Frutas' },
                { name: 'Naranjas', price: '$850', unit: 'bolsa', stock: 12, category: 'Frutas' }
              ].map((item) => (
                <div key={item.name} className="bg-gray-50 p-2 rounded-lg">
                  <div className="text-xs text-[#3B7C5B]">{item.category}</div>
                  <div className="font-medium text-sm">{item.name}</div>
                  <div className="text-[#3B7C5B] font-bold text-sm">{item.price}</div>
                  <div className="text-xs text-gray-500">{item.unit}</div>
                  <div className={`text-xs ${item.stock > 10 ? 'text-green-600' : 'text-orange-500'}`}>
                    Stock: {item.stock}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="p-2 border-t">
            <button className="w-full bg-[#3B7C5B] text-white py-1.5 rounded-lg text-sm flex items-center justify-center gap-2">
              <Share2 className="w-4 h-4" />
              Compartir catálogo
            </button>
          </div>
        </div>
      )
    },
    {
      title: "Registra ventas rápido",
      description: "Proceso de venta optimizado con calculadora integrada y múltiples formas de pago. Registra ventas en segundos y mantén un control total.",
      content: (
        <div className="h-full flex flex-col">
          <div className="bg-[#3B7C5B] text-white py-2 px-3 text-xs">
            <span>Venta rápida</span>
          </div>
          <div className="flex-1 p-3 space-y-2">
            <div className="bg-gray-50 p-2 rounded-lg">
              <div className="text-xs text-gray-500">Producto seleccionado</div>
              <div className="font-medium">Tomates</div>
              <div className="text-xs text-[#3B7C5B]">$950/cajón</div>
            </div>
            <div className="bg-gray-50 p-2 rounded-lg">
              <div className="text-xs text-gray-500">Cantidad</div>
              <div className="text-lg font-bold text-[#3B7C5B]">3 cajones</div>
              <div className="text-xs text-gray-500">Stock disponible: 15</div>
            </div>
            <div className="bg-gray-50 p-2 rounded-lg">
              <div className="text-xs text-gray-500">Total a cobrar</div>
              <div className="text-xl font-bold">$2,850</div>
              <div className="text-xs text-[#3B7C5B]">Efectivo</div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-0.5 p-1 bg-gray-50">
            {[1,2,3,4,5,6,7,8,9,'C',0,'✓'].map((key) => (
              <button key={key} className={`
                aspect-square rounded text-sm font-medium
                ${key === 'C' ? 'bg-red-100 text-red-700' : 
                  key === '✓' ? 'bg-[#3B7C5B]/10 text-[#3B7C5B]' : 
                  'bg-white text-gray-700'}
              `}>
                {key}
              </button>
            ))}
          </div>
        </div>
      )
    },
    {
      title: "Imprime tickets",
      description: "Genera tickets profesionales personalizados con tu marca. Incluye original y copias para cliente y negocio automáticamente.",
      content: ({ title, description }) => (
        <div className="h-[600px] flex flex-col items-center pt-48">
          <div className="relative w-48">
            <div className="absolute left-1/2 -top-0 transform -translate-x-1/2 w-full">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="absolute left-1/2 transform -translate-x-1/2 transition-all duration-1000"
                  style={{
                    bottom: `${i * 30}px`,
                    width: '70%',
                    zIndex: 3 - i,
                    animation: `printTicket${i} 5s ease-out infinite`,
                  }}
                >
                  <div 
                    className="bg-white p-2 border border-gray-300 shadow-sm"
                    style={{
                      transform: `rotate(${i * 1 - 1}deg)`,
                      borderTop: '1px dashed #e5e7eb',
                    }}
                  >
                    <div className="space-y-1 text-center" style={{ fontFamily: 'monospace' }}>
                      <div className="border-b border-dotted pb-1 mb-1 flex justify-between items-center">
                        <div className="text-[10px] font-bold">ORDEN #1</div>
                        <div className="text-[8px] text-gray-500">13:45</div>
                      </div>
                      
                      <div className="space-y-1">
                        <div className="text-[8px] text-gray-500">PRODUCTO</div>
                        <div className="text-xs font-bold tracking-tight">Tomates</div>
                        <div className="flex justify-between items-center text-[10px]">
                          <span className="text-gray-500">3 cajones</span>
                          <span className="font-bold">$2,850</span>
                        </div>
                      </div>

                      <div className="border-t border-dotted pt-1 mt-1">
                        <div className="text-[8px] text-gray-500">
                          {i === 2 ? 'COPIA CLIENTE' : i === 1 ? 'COPIA DESPACHO' : 'COPIA COMERCIO'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="relative">
              <div className="bg-neutral-900 rounded-t-xl h-4 relative">
                <div className="absolute inset-x-3 top-1/2 transform -translate-y-1/2 h-[2px] bg-neutral-800"></div>
              </div>

              <div className="bg-neutral-900 px-3 py-4 space-y-3">
                <div className="flex justify-center gap-2">
                  <div className="w-1 h-1 rounded-full bg-green-500 animate-[pulse_2s_ease-in-out_infinite]"></div>
                  <div className="w-1 h-1 rounded-full bg-yellow-500"></div>
                  <div className="w-1 h-1 rounded-full bg-red-500"></div>
                </div>

                <div className="space-y-3">
                  <div className="h-1 bg-neutral-800 mx-4"></div>
                  
                  <div className="flex justify-center gap-2">
                    <button className="w-8 h-8 rounded bg-neutral-800 flex items-center justify-center hover:bg-neutral-700 transition-colors">
                      <Printer className="w-4 h-4 text-neutral-400" />
                    </button>
                    <button className="w-8 h-8 rounded bg-neutral-800 flex items-center justify-center hover:bg-neutral-700 transition-colors">
                      <Receipt className="w-4 h-4 text-neutral-400" />
                    </button>
                  </div>
                </div>

                <div className="relative h-12">
                  <div className="absolute inset-x-2 top-0 h-full bg-neutral-800 rounded-b-lg">
                    <div className="absolute inset-x-2 top-1/2 h-4 bg-neutral-700 rounded"></div>
                  </div>
                </div>
              </div>

              <div className="bg-neutral-950 h-2 rounded-b-xl shadow-lg"></div>

              <div className="absolute -right-3 top-8 h-16 w-1 bg-neutral-800 rounded-r"></div>
              <div className="absolute -left-3 top-8 h-16 w-1 bg-neutral-800 rounded-l"></div>
            </div>

            <style jsx>{`
              @keyframes printTicket0 {
                0% { transform: translate(-50%, 100%); opacity: 0; }
                40% { transform: translate(-50%, 100%); opacity: 0; }
                60% { transform: translate(-50%, 0); opacity: 1; }
                100% { transform: translate(-50%, 0); opacity: 1; }
              }
              @keyframes printTicket1 {
                0% { transform: translate(-50%, 100%); opacity: 0; }
                20% { transform: translate(-50%, 100%); opacity: 0; }
                40% { transform: translate(-50%, 0); opacity: 1; }
                100% { transform: translate(-50%, 0); opacity: 1; }
              }
              @keyframes printTicket2 {
                0% { transform: translate(-50%, 100%); opacity: 0; }
                0% { transform: translate(-50%, 100%); opacity: 0; }
                20% { transform: translate(-50%, 0); opacity: 1; }
                100% { transform: translate(-50%, 0); opacity: 1; }
              }
            `}</style>
          </div>
        </div>
      )
    }
  ];

  const steps = [
    {
      title: "Onboarding simple",
      description: "Escanea el código QR, ingresa los datos de tu negocio y comienza a usar Tibó en menos de 2 minutos.",
      icon: <QrCode className="w-24 h-24 text-green-700" strokeWidth={1.5} />
    },
    {
      title: "Carga tu inventario",
      description: "Agrega tus productos, precios y cantidades. Tibó te ayuda a mantener todo organizado y actualizado.",
      icon: <Boxes className="w-24 h-24 text-green-700" strokeWidth={1.5} />
    },
    {
      title: "Registra ventas",
      description: "Realiza ventas rápidas, imprime tickets y mantén el control de tu negocio desde cualquier dispositivo.",
      icon: <Receipt className="w-24 h-24 text-green-700" strokeWidth={1.5} />
    }
  ];

  return (
    <div className={`min-h-screen ${isDark ? 'dark' : ''}`}>
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm border-b border-gray-100 dark:border-gray-800">
        <div className="container mx-auto px-4">
          <div className="h-16 flex items-center justify-between">
            <div className="text-xl font-bold text-[#3B7C5B] dark:text-white">
              Tibó
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#how-it-works" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">Cómo funciona</a>
              <a href="#benefits" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">Beneficios</a>
              <a href="#pricing" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">Precios</a>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setIsDark(!isDark)}
                className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                aria-label="Cambiar tema"
              >
                {isDark ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </button>
              <button className="bg-[#3B7C5B] text-white px-4 py-2 rounded-lg hover:bg-[#2E6346] transition-colors">
                Empezar gratis
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-[600px] overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img 
            src={HeroImage} 
            alt="Mayorista usando Tibó" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/50 dark:from-gray-900 dark:via-gray-900/95 dark:to-gray-900/50" />
        </div>
        
        {/* Contenido */}
        <div className="relative container mx-auto px-4 py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Texto principal */}
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#3B7C5B]/10 border border-[#3B7C5B]/20 backdrop-blur-sm">
                <span className="flex h-2 w-2 rounded-full bg-[#3B7C5B]"></span>
                <span className="text-sm font-medium text-[#3B7C5B] dark:text-white">Software de gestión</span>
              </div>
              
              <div className="space-y-6">
                <h1 className="text-6xl font-bold tracking-tight text-gray-900 dark:text-white">
                  Control total para{' '}
                  <span className="text-[#3B7C5B] relative">
                    mayoristas
                    <span className="absolute -bottom-2 left-0 right-0 h-1 bg-[#3B7C5B]/30 rounded-full"></span>
                  </span>
                </h1>
                <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl">
                  Gestiona tu negocio mayorista de manera eficiente y profesional. 
                  Inventario, ventas y entregas en un solo lugar.
                </p>
              </div>

              <div className="flex items-center gap-4">
                <button className="bg-[#3B7C5B] text-white px-8 py-4 rounded-xl text-lg font-medium hover:bg-[#2E6346] transition-colors inline-flex items-center gap-2 shadow-lg shadow-[#3B7C5B]/25 hover:shadow-xl hover:shadow-[#3B7C5B]/30">
                  Empezar gratis
                  <ArrowRight className="w-5 h-5" />
                </button>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  No requiere tarjeta de crédito
                </span>
              </div>
            </div>

            {/* Phone Preview */}
            <div className="hidden lg:block relative">
              <div className="aspect-[9/19] w-[280px] mx-auto bg-black rounded-[2.5rem] p-2 relative shadow-xl">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/3 h-5 bg-black rounded-b-2xl"></div>
                <div className="w-full h-full bg-white dark:bg-gray-900 rounded-[2.25rem] overflow-hidden">
                  {phones[0].content}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section className="py-24 bg-white" id="how-it-works">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold mb-6">Empezá a vender más en 3 pasos</h2>
            <p className="text-xl text-gray-600">
              Sin complicaciones, sin instalaciones, sin demoras. 
              Comenzá a usar Tibó hoy mismo y transformá tu negocio mayorista.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                step: "1",
                title: "Cargá tu inventario",
                description: "En 5 minutos subís todos tus productos. Importá desde Excel o cargá manualmente con nuestro sistema intuitivo. ¡Tu catálogo listo al instante!",
                icon: <Package className="w-12 h-12" />,
                color: "bg-blue-50",
                iconColor: "text-blue-600",
                features: [
                  "Carga masiva desde Excel",
                  "Fotos de productos",
                  "Categorías automáticas",
                  "Precios y stock"
                ],
                action: {
                  text: "Ver demo de carga",
                  link: "#demo"
                }
              },
              {
                step: "2",
                title: "Compartí tu catálogo",
                description: "Tu catálogo digital listo para compartir por WhatsApp. Tus clientes ven productos actualizados en tiempo real y te contactan con un clic.",
                icon: <Share2 className="w-12 h-12" />,
                color: "bg-green-50",
                iconColor: "text-green-600",
                features: [
                  "Link público personalizado",
                  "Código QR para tu local",
                  "Actualización en tiempo real",
                  "Chat directo de WhatsApp"
                ],
                action: {
                  text: "Ver ejemplo de catálogo",
                  link: "#catalog"
                }
              },
              {
                step: "3",
                title: "Gestioná todo desde un lugar",
                description: "Controlá ventas, stock y entregas desde cualquier dispositivo. Tibó se encarga de todo mientras vos te enfocás en vender.",
                icon: <BarChart className="w-12 h-12" />,
                color: "bg-purple-50",
                iconColor: "text-purple-600",
                features: [
                  "Panel de control unificado",
                  "Reportes en tiempo real",
                  "Control de pagos",
                  "Gestión de entregas"
                ],
                action: {
                  text: "Ver panel de control",
                  link: "#dashboard"
                }
              }
            ].map((item, i) => (
              <div key={i} className="relative group">
                {/* Step Number */}
                <div className={`absolute -top-6 right-4 w-12 h-12 rounded-full ${item.color} ${item.iconColor} flex items-center justify-center font-bold text-2xl border-2 border-white shadow-lg`}>
                  {item.step}
                </div>

                {/* Card */}
                <div className={`h-full ${item.color} rounded-2xl p-8 transition-all group-hover:scale-[1.02]`}>
                  <div className={`${item.iconColor} mb-6`}>
                    {item.icon}
                  </div>

                  <h3 className="text-2xl font-bold mb-4">{item.title}</h3>
                  <p className="text-gray-600 mb-6">{item.description}</p>

                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {item.features.map((feature, j) => (
                      <li key={j} className="flex items-center gap-2">
                        <CheckCircle2 className={`w-5 h-5 ${item.iconColor}`} />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Action Button */}
                  <a
                    href={item.action.link}
                    className={`inline-flex items-center gap-2 text-sm font-medium ${item.iconColor} hover:underline`}
                  >
                    {item.action.text}
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="mt-16 text-center">
            <button className="bg-[#3B7C5B] text-white px-8 py-4 rounded-xl text-lg font-medium hover:bg-[#2E6346] transition-colors inline-flex items-center gap-2 shadow-lg shadow-[#3B7C5B]/25 hover:shadow-xl hover:shadow-[#3B7C5B]/30">
              Empezar ahora gratis
              <ArrowRight className="w-5 h-5" />
            </button>
            <p className="mt-4 text-gray-500">No necesitás tarjeta de crédito</p>
          </div>
        </div>
      </section>

      {/* Phones Section */}
      <section className="py-24 bg-gray-50 dark:bg-gray-900" id="features">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white">Tibó en acción</h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 text-center mt-4 mb-16 max-w-2xl mx-auto">
            Mirá cómo Tibó transforma tu negocio mayorista con herramientas simples y poderosas.
            Diseñado para que vos y tu equipo puedan enfocarse en lo importante: vender más.
          </p>

          {/* Desktop Grid */}
          <div className="hidden md:grid md:grid-cols-3 gap-12">
            {phones.map((phone, i) => (
              <div key={i} className="relative group">
                {/* Step Number */}
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-[#3B7C5B] text-white flex items-center justify-center font-bold text-2xl shadow-lg">
                  {i + 1}
                </div>

                {/* Phone or Content */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg space-y-6">
                  {/* Title and Description */}
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">{phone.title}</h3>
                    <p className="text-gray-600 dark:text-gray-300 text-lg">
                      {phone.description}
                    </p>
                  </div>

                  {/* Phone Preview */}
                  <div className="relative">
                    {i === 2 ? (
                      <div className="w-full max-w-[320px] mx-auto">
                        {phone.content({
                          title: phone.title,
                          description: phone.description
                        })}
                      </div>
                    ) : (
                      <div className="aspect-[9/19] w-56 mx-auto bg-black rounded-[2.5rem] p-2 relative shadow-xl">
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/3 h-5 bg-black rounded-b-2xl"></div>
                        <div className="w-full h-full bg-white dark:bg-gray-900 rounded-[2.25rem] overflow-hidden">
                          {phone.content}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile Carousel */}
          <div className="md:hidden">
            <div className="relative">
              {activePhone === 2 ? (
                <div className="w-full max-w-[280px] mx-auto">
                  {phones[activePhone].content({
                    title: phones[activePhone].title,
                    description: phones[activePhone].description
                  })}
                </div>
              ) : (
                <div className="aspect-[9/19] w-48 mx-auto bg-black rounded-[2rem] p-2 relative shadow-xl">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/3 h-4 bg-black rounded-b-2xl"></div>
                  <div className="w-full h-full bg-white dark:bg-gray-900 rounded-[1.75rem] overflow-hidden">
                    {phones[activePhone].content}
                  </div>
                </div>
              )}
              
              {/* Navigation Buttons */}
              <button 
                onClick={() => setActivePhone((prev) => (prev - 1 + phones.length) % phones.length)}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white dark:bg-gray-800 rounded-full p-2 shadow-lg"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button 
                onClick={() => setActivePhone((prev) => (prev + 1) % phones.length)}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white dark:bg-gray-800 rounded-full p-2 shadow-lg"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            
            {/* Title and Description */}
            <div className="mt-12 text-center">
              <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">{phones[activePhone].title}</h3>
              <p className="text-gray-600 dark:text-gray-300 text-lg max-w-sm mx-auto">
                {phones[activePhone].description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white" id="features">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16">Funcionalidades</h2>
          
          <div className="relative">
            {/* Main Card */}
            <div className="bg-gray-50 rounded-2xl overflow-hidden shadow-lg">
              <div className="grid md:grid-cols-2">
                {/* Left side - Illustration */}
                <div className="relative aspect-square md:aspect-auto p-12 flex items-center justify-center bg-gradient-to-br from-[#3B7C5B]/10 to-transparent">
                  {[
                    {
                      title: "Control de inventario",
                      description: "Mantené el control total de tus productos, precios y stock en tiempo real. Organizá por categorías y actualizá todo desde cualquier dispositivo.",
                      illustration: (
                        <div className="w-full max-w-md">
                          <div className="relative">
                            <div className="absolute inset-0 bg-[#3B7C5B]/5 rounded-xl transform rotate-6"></div>
                            <div className="relative bg-white rounded-xl p-8 shadow-md">
                              <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-4">
                                  <div className="h-20 bg-[#3B7C5B]/10 rounded-lg flex items-center justify-center">
                                    <Package className="w-8 h-8 text-[#3B7C5B]" />
                                  </div>
                                  <div className="h-2 bg-[#3B7C5B]/20 rounded-full w-2/3"></div>
                                  <div className="h-2 bg-[#3B7C5B]/10 rounded-full w-1/2"></div>
                                </div>
                                <div className="space-y-4">
                                  <div className="h-20 bg-[#3B7C5B]/10 rounded-lg flex items-center justify-center">
                                    <Boxes className="w-8 h-8 text-[#3B7C5B]" />
                                  </div>
                                  <div className="h-2 bg-[#3B7C5B]/20 rounded-full w-3/4"></div>
                                  <div className="h-2 bg-[#3B7C5B]/10 rounded-full w-1/2"></div>
                                </div>
                              </div>
                              <div className="mt-6 flex justify-between items-center">
                                <div className="flex space-x-2">
                                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                </div>
                                <div className="h-2 bg-[#3B7C5B]/20 rounded-full w-20"></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    },
                    {
                      title: "Ventas rápidas",
                      description: "Registrá ventas en segundos con nuestra calculadora integrada. Múltiples formas de pago y control total de las transacciones.",
                      illustration: (
                        <div className="w-full max-w-md">
                          <div className="relative">
                            <div className="absolute inset-0 bg-[#3B7C5B]/5 rounded-xl transform -rotate-3"></div>
                            <div className="relative bg-white rounded-xl p-8 shadow-md">
                              <div className="space-y-6">
                                <div className="bg-[#3B7C5B]/10 rounded-lg p-4">
                                  <div className="flex justify-between items-center">
                                    <DollarSign className="w-8 h-8 text-[#3B7C5B]" />
                                    <div className="text-2xl font-bold text-[#3B7C5B]">$2,850</div>
                                  </div>
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                  {[1,2,3,4,5,6,7,8,9,'C',0,'✓'].map((key) => (
                                    <div key={key} className="aspect-square rounded-lg bg-[#3B7C5B]/10 flex items-center justify-center text-[#3B7C5B] font-medium">
                                      {key}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    },
                    {
                      title: "Impresión de tickets",
                      description: "Generá tickets profesionales personalizados con tu marca. Original y copias para cliente y negocio automáticamente.",
                      illustration: (
                        <div className="w-full max-w-md">
                          <div className="relative">
                            <div className="absolute inset-0 bg-[#3B7C5B]/5 rounded-xl transform rotate-3"></div>
                            <div className="relative bg-white rounded-xl p-8 shadow-md">
                              <div className="space-y-4">
                                <div className="bg-neutral-900 rounded-lg p-4">
                                  <div className="flex justify-center gap-2 mb-4">
                                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                    <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                                    <div className="w-2 h-2 rounded-full bg-red-500"></div>
                                  </div>
                                  <div className="bg-neutral-800 h-1 rounded-full"></div>
                                </div>
                                <div className="relative">
                                  {[0, 1].map((i) => (
                                    <div
                                      key={i}
                                      className="bg-white p-3 rounded border shadow-sm mb-2"
                                      style={{
                                        transform: `rotate(${i * 2 - 1}deg)`,
                                      }}
                                    >
                                      <div className="space-y-2">
                                        <div className="h-2 bg-[#3B7C5B]/20 rounded-full w-1/2"></div>
                                        <div className="h-2 bg-[#3B7C5B]/10 rounded-full w-3/4"></div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    },
                    {
                      title: "Catálogo digital",
                      description: "Compartí tu catálogo actualizado por WhatsApp. Tus clientes ven tus productos con fotos y precios en tiempo real.",
                      illustration: (
                        <div className="w-full max-w-md">
                          <div className="relative">
                            <div className="absolute inset-0 bg-[#3B7C5B]/5 rounded-xl transform -rotate-6"></div>
                            <div className="relative bg-white rounded-xl p-8 shadow-md">
                              <div className="space-y-4">
                                <div className="flex items-center gap-4">
                                  <div className="w-16 h-16 bg-[#3B7C5B]/10 rounded-lg flex items-center justify-center">
                                    <MessageSquare className="w-8 h-8 text-[#3B7C5B]" />
                                  </div>
                                  <div className="flex-1 space-y-2">
                                    <div className="h-2 bg-[#3B7C5B]/20 rounded-full w-3/4"></div>
                                    <div className="h-2 bg-[#3B7C5B]/10 rounded-full w-1/2"></div>
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                  {[1,2].map((i) => (
                                    <div key={i} className="bg-[#3B7C5B]/5 p-4 rounded-lg space-y-2">
                                      <div className="h-2 bg-[#3B7C5B]/20 rounded-full w-2/3"></div>
                                      <div className="h-2 bg-[#3B7C5B]/10 rounded-full w-1/2"></div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    },
                    {
                      title: "Gestión de equipo",
                      description: "Invitá a tu equipo y asigná roles específicos. Cada miembro accede solo a lo que necesita desde cualquier dispositivo.",
                      illustration: (
                        <div className="w-full max-w-md">
                          <div className="relative">
                            <div className="absolute inset-0 bg-[#3B7C5B]/5 rounded-xl transform rotate-6"></div>
                            <div className="relative bg-white rounded-xl p-8 shadow-md">
                              <div className="space-y-6">
                                <div className="flex justify-center gap-4">
                                  {[1,2,3].map((i) => (
                                    <div key={i} className="w-16 h-16 bg-[#3B7C5B]/10 rounded-full flex items-center justify-center">
                                      <Users className="w-8 h-8 text-[#3B7C5B]" />
                                    </div>
                                  ))}
                                </div>
                                <div className="space-y-3">
                                  <div className="h-2 bg-[#3B7C5B]/20 rounded-full"></div>
                                  <div className="h-2 bg-[#3B7C5B]/10 rounded-full w-3/4"></div>
                                  <div className="h-2 bg-[#3B7C5B]/20 rounded-full w-1/2"></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    }
                  ].map((feature, index) => (
                    <div
                      key={index}
                      className={`absolute inset-0 transition-opacity duration-500 ${
                        index === activeFeature ? 'opacity-100' : 'opacity-0 pointer-events-none'
                      }`}
                    >
                      {feature.illustration}
                    </div>
                  ))}
                </div>

                {/* Right side - Content */}
                <div className="p-12 flex flex-col justify-center">
                  {[
                    {
                      title: "Control de inventario",
                      description: "Mantené el control total de tus productos, precios y stock en tiempo real. Organizá por categorías y actualizá todo desde cualquier dispositivo."
                    },
                    {
                      title: "Ventas rápidas",
                      description: "Registrá ventas en segundos con nuestra calculadora integrada. Múltiples formas de pago y control total de las transacciones."
                    },
                    {
                      title: "Impresión de tickets",
                      description: "Generá tickets profesionales personalizados con tu marca. Original y copias para cliente y negocio automáticamente."
                    },
                    {
                      title: "Catálogo digital",
                      description: "Compartí tu catálogo actualizado por WhatsApp. Tus clientes ven tus productos con fotos y precios en tiempo real."
                    },
                    {
                      title: "Gestión de equipo",
                      description: "Invitá a tu equipo y asigná roles específicos. Cada miembro accede solo a lo que necesita desde cualquier dispositivo."
                    }
                  ].map((feature, index) => (
                    <div
                      key={index}
                      className={`transition-opacity duration-500 ${
                        index === activeFeature ? 'opacity-100' : 'opacity-0 absolute pointer-events-none'
                      }`}
                    >
                      <h3 className="text-2xl font-bold mb-4">{feature.title}</h3>
                      <p className="text-gray-600 text-lg leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  ))}

                  {/* Navigation Dots */}
                  <div className="flex justify-center gap-3 mt-12">
                    {[0,1,2,3,4].map((i) => (
                      <button
                        key={i}
                        onClick={() => setActiveFeature(i)}
                        className={`w-3 h-3 rounded-full transition-all duration-300 ${
                          i === activeFeature 
                            ? 'bg-[#3B7C5B] w-6' 
                            : 'bg-[#3B7C5B]/20 hover:bg-[#3B7C5B]/40'
                        }`}
                        aria-label={`Ver característica ${i + 1}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={() => setActiveFeature((prev) => (prev - 1 + 5) % 5)}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center text-gray-600 hover:text-[#3B7C5B] transition-colors"
              aria-label="Característica anterior"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={() => setActiveFeature((prev) => (prev + 1) % 5)}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center text-gray-600 hover:text-[#3B7C5B] transition-colors"
              aria-label="Siguiente característica"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>
      </section>

      {/* Tres Pasos Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4">¡Sumá a tu equipo en segundos!</h2>
          <p className="text-xl text-gray-600 text-center mb-16 max-w-2xl mx-auto">
            Sin complicaciones, sin esperas. Tu equipo conectado al instante.
          </p>
          
          <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-12">
            {/* Paso 1 - QR Scanning */}
            <div className="relative">
              <div className="bg-white rounded-xl p-8 text-center shadow-lg border border-gray-100">
                {/* Ilustración */}
                <div className="mb-8 relative h-48">
                  {/* Phone 1 - Showing QR */}
                  <div className="absolute left-8 top-1/2 -translate-y-1/2 w-32 h-56 bg-gray-900 rounded-3xl p-2">
                    <div className="w-full h-full bg-white rounded-2xl p-4 flex flex-col">
                      <div className="flex-1 bg-[#3B7C5B]/5 rounded-lg flex items-center justify-center">
                        <div className="w-20 h-20 bg-white p-1">
                          <div className="w-full h-full bg-[#3B7C5B] rounded-sm grid grid-cols-4 grid-rows-4 gap-0.5 p-1">
                            {Array(16).fill(null).map((_, i) => (
                              <div key={i} className="bg-white rounded-sm"></div>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 space-y-1">
                        <div className="h-2 bg-gray-200 rounded-full w-3/4 mx-auto"></div>
                        <div className="h-2 bg-gray-200 rounded-full w-1/2 mx-auto"></div>
                      </div>
                    </div>
                  </div>
                  {/* Phone 2 - Scanning */}
                  <div className="absolute right-8 top-1/2 -translate-y-1/2 w-32 h-56 bg-gray-900 rounded-3xl p-2 transform rotate-12">
                    <div className="w-full h-full bg-white rounded-2xl overflow-hidden">
                      <div className="h-full w-full bg-[#3B7C5B]/5 flex items-center justify-center">
                        <div className="w-24 h-24 border-2 border-[#3B7C5B] rounded-lg relative">
                          <div className="absolute inset-0 bg-[#3B7C5B]/10"></div>
                          <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-[#3B7C5B] animate-[pulse_2s_ease-in-out_infinite]"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <h3 className="text-xl font-bold mb-4">1. Compartí el acceso</h3>
                <p className="text-gray-600">
                  Copiá el link o mostrá el QR de tu negocio a los miembros de tu equipo
                </p>
              </div>
              <div className="hidden md:block absolute -right-6 top-1/2 -translate-y-1/2 z-10">
                <ArrowRight className="w-6 h-6 text-[#3B7C5B]" />
              </div>
            </div>

            {/* Paso 2 - Role Selection */}
            <div className="relative">
              <div className="bg-white rounded-xl p-8 text-center shadow-lg border border-gray-100">
                {/* Ilustración */}
                <div className="mb-8 relative h-48">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-64 bg-white rounded-xl shadow-lg border border-gray-100">
                      <div className="p-4 border-b border-gray-100">
                        <div className="text-left">
                          <div className="h-4 bg-gray-100 rounded w-1/2 mb-2"></div>
                          <div className="h-3 bg-gray-50 rounded w-3/4"></div>
                        </div>
                      </div>
                      {["Admin", "Vendedor", "Cajero", "Peón"].map((role, i) => (
                        <div key={role} className={`p-4 flex items-center gap-3 border-b border-gray-50 ${i === 1 ? 'bg-[#3B7C5B]/5' : ''}`}>
                          <div className={`w-4 h-4 rounded-full border-2 ${i === 1 ? 'border-[#3B7C5B] bg-[#3B7C5B]' : 'border-gray-300'}`}>
                            {i === 1 && <div className="w-1.5 h-1.5 bg-white rounded-full mx-auto mt-0.5"></div>}
                          </div>
                          <span className={`text-sm ${i === 1 ? 'text-[#3B7C5B] font-medium' : 'text-gray-600'}`}>{role}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <h3 className="text-xl font-bold mb-4">2. Asigná roles</h3>
                <p className="text-gray-600">
                  Definí qué puede hacer cada miembro del equipo con un click
                </p>
              </div>
              <div className="hidden md:block absolute -right-6 top-1/2 -translate-y-1/2 z-10">
                <ArrowRight className="w-6 h-6 text-[#3B7C5B]" />
              </div>
            </div>

            {/* Paso 3 - Connected Team */}
            <div className="relative">
              <div className="bg-white rounded-xl p-8 text-center shadow-lg border border-gray-100">
                {/* Ilustración */}
                <div className="mb-8 relative h-48">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="relative">
                      {/* Connected circles representing team members */}
                      {[
                        { x: 'center', y: 'top' },
                        { x: 'left', y: 'middle' },
                        { x: 'right', y: 'middle' },
                        { x: 'center', y: 'bottom' }
                      ].map((pos, i) => (
                        <div
                          key={i}
                          className={`absolute w-16 h-16 bg-[#3B7C5B]/10 rounded-full flex items-center justify-center
                            ${pos.x === 'center' ? 'left-1/2 -translate-x-1/2' : pos.x === 'left' ? 'left-0' : 'right-0'}
                            ${pos.y === 'top' ? 'top-0' : pos.y === 'middle' ? 'top-1/2 -translate-y-1/2' : 'bottom-0'}
                          `}
                        >
                          <Users className="w-8 h-8 text-[#3B7C5B]" />
                          {/* Connection lines */}
                          {i > 0 && (
                            <div className="absolute inset-0 w-full h-full">
                              <div className="absolute top-1/2 left-1/2 w-16 h-0.5 bg-[#3B7C5B]/20 origin-left"
                                style={{
                                  transform: `rotate(${i * 120}deg)`,
                                }}
                              >
                                <div className="absolute right-0 w-1.5 h-1.5 bg-[#3B7C5B] rounded-full -translate-y-1/2"></div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                      {/* Success indicator */}
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-white rounded-full shadow-lg border-4 border-[#3B7C5B] flex items-center justify-center">
                        <CheckCircle2 className="w-10 h-10 text-[#3B7C5B]" />
                      </div>
                    </div>
                  </div>
                </div>

                <h3 className="text-xl font-bold mb-4">3. ¡Listo!</h3>
                <p className="text-gray-600">
                  Tu equipo ya está en la misma página y listo para trabajar
                </p>
              </div>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="mt-12 text-center">
            <button className="bg-[#3B7C5B] text-white px-6 py-3 rounded-xl text-lg font-medium hover:bg-[#2E6346] transition-colors inline-flex items-center gap-2">
              Probalo gratis
              <ArrowRight className="w-5 h-5" />
            </button>
            <p className="mt-4 text-gray-500">No necesitás tarjeta de crédito</p>
          </div>
        </div>
      </section>

      {/* Carga de Inventario Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4">¡Cargá tu inventario en minutos!</h2>
          <p className="text-xl text-gray-600 text-center mb-16 max-w-2xl mx-auto">
            Empezá a usar Tibó con todo tu catálogo de productos en tres simples pasos.
          </p>
          
          <div className="max-w-4xl mx-auto grid md:grid-cols-3 gap-8">
            {/* Paso 1 */}
            <div className="relative">
              <div className="bg-white rounded-xl p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-[#3B7C5B]/10 text-[#3B7C5B] flex items-center justify-center mx-auto mb-6">
                  <Package className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold mb-4">1. Importá tu Excel</h3>
                <p className="text-gray-600">
                  Subí tu lista de productos desde Excel o cargalos manualmente uno por uno
                </p>
              </div>
              <div className="hidden md:block absolute -right-8 top-1/2 -translate-y-1/2 z-10">
                <ArrowRight className="w-6 h-6 text-[#3B7C5B]" />
              </div>
            </div>

            {/* Paso 2 */}
            <div className="relative">
              <div className="bg-white rounded-xl p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-[#3B7C5B]/10 text-[#3B7C5B] flex items-center justify-center mx-auto mb-6">
                  <Search className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold mb-4">2. Revisá y organizá</h3>
                <p className="text-gray-600">
                  Verificá los datos y organizá tus productos en categorías
                </p>
              </div>
              <div className="hidden md:block absolute -right-8 top-1/2 -translate-y-1/2 z-10">
                <ArrowRight className="w-6 h-6 text-[#3B7C5B]" />
              </div>
            </div>

            {/* Paso 3 */}
            <div className="bg-white rounded-xl p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-[#3B7C5B]/10 text-[#3B7C5B] flex items-center justify-center mx-auto mb-6">
                <Share2 className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-4">3. ¡A vender!</h3>
              <p className="text-gray-600">
                Compartí tu catálogo digital y empezá a vender al instante
              </p>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="mt-12 text-center">
            <button className="bg-[#3B7C5B] text-white px-6 py-3 rounded-xl text-lg font-medium hover:bg-[#2E6346] transition-colors inline-flex items-center gap-2">
              Probalo gratis
              <ArrowRight className="w-5 h-5" />
            </button>
            <p className="mt-4 text-gray-500">Importá hasta 200 productos gratis</p>
          </div>
        </div>
      </section>

      {/* Registro de Ventas Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4">Registrá ventas en segundos</h2>
          <p className="text-xl text-gray-600 text-center mb-16 max-w-2xl mx-auto">
            Proceso de venta optimizado para atender rápido a tus clientes.
          </p>
          
          <div className="max-w-4xl mx-auto grid md:grid-cols-3 gap-8">
            {/* Paso 1 */}
            <div className="relative">
              <div className="bg-white rounded-xl p-8 text-center shadow-md">
                <div className="w-16 h-16 rounded-full bg-[#3B7C5B]/10 text-[#3B7C5B] flex items-center justify-center mx-auto mb-6">
                  <Search className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold mb-4">1. Elegí productos</h3>
                <p className="text-gray-600">
                  Buscá rápido por nombre o escaneá códigos de barras
                </p>
              </div>
              <div className="hidden md:block absolute -right-8 top-1/2 -translate-y-1/2 z-10">
                <ArrowRight className="w-6 h-6 text-[#3B7C5B]" />
              </div>
            </div>

            {/* Paso 2 */}
            <div className="relative">
              <div className="bg-white rounded-xl p-8 text-center shadow-md">
                <div className="w-16 h-16 rounded-full bg-[#3B7C5B]/10 text-[#3B7C5B] flex items-center justify-center mx-auto mb-6">
                  <DollarSign className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold mb-4">2. Cobrá</h3>
                <p className="text-gray-600">
                  Calculadora integrada y múltiples formas de pago
                </p>
              </div>
              <div className="hidden md:block absolute -right-8 top-1/2 -translate-y-1/2 z-10">
                <ArrowRight className="w-6 h-6 text-[#3B7C5B]" />
              </div>
            </div>

            {/* Paso 3 */}
            <div className="bg-white rounded-xl p-8 text-center shadow-md">
              <div className="w-16 h-16 rounded-full bg-[#3B7C5B]/10 text-[#3B7C5B] flex items-center justify-center mx-auto mb-6">
                <Printer className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-4">3. Imprimí ticket</h3>
              <p className="text-gray-600">
                Original y copias personalizadas con tu marca
              </p>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="mt-12 text-center">
            <button className="bg-[#3B7C5B] text-white px-6 py-3 rounded-xl text-lg font-medium hover:bg-[#2E6346] transition-colors inline-flex items-center gap-2">
              Ver demo
              <ArrowRight className="w-5 h-5" />
            </button>
            <p className="mt-4 text-gray-500">Ventas ilimitadas sin cargo extra</p>
          </div>
        </div>
      </section>

      {/* WhatsApp Integration */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-3xl font-bold">Catálogo digital en WhatsApp</h2>
                <p className="text-xl text-gray-600">
                  Comparte tu catálogo actualizado con todos tus clientes. Un link público que muestra tus productos con fotos y se actualiza en tiempo real.
                </p>
              </div>

              <div className="space-y-6">
                <div className="bg-white rounded-xl p-6 space-y-4">
                  <h3 className="text-lg font-bold flex items-center gap-2">
                    <Package className="w-5 h-5 text-[#3B7C5B]" />
                    Catálogo inteligente
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#3B7C5B] flex-shrink-0 mt-1" />
                      <p className="text-gray-600">Actualización automática cuando modificas tu inventario</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#3B7C5B] flex-shrink-0 mt-1" />
                      <p className="text-gray-600">Control sobre qué información mostrar (precios, stock, etc.)</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#3B7C5B] flex-shrink-0 mt-1" />
                      <p className="text-gray-600">Fotos de productos y descripciones detalladas</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-6 space-y-4">
                  <h3 className="text-lg font-bold flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-[#25D366]" />
                    Pedidos simplificados
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#25D366] flex-shrink-0 mt-1" />
                      <p className="text-gray-600">Tus clientes pueden contactarte directamente desde el catálogo</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#25D366] flex-shrink-0 mt-1" />
                      <p className="text-gray-600">Un solo link para compartir con todos tus clientes</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#25D366] flex-shrink-0 mt-1" />
                      <p className="text-gray-600">Registra los pedidos manualmente en el sistema</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <button 
                  onClick={onShowCatalog}
                  className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 rounded-xl font-medium hover:bg-[#20BD5A] transition-colors"
                >
                  <MessageSquare className="w-5 h-5" />
                  Ver ejemplo de catálogo
                </button>
                <span className="text-sm text-gray-500">
                  Actualizado en tiempo real
                </span>
              </div>
            </div>

            <div className="relative">
              <div className="aspect-[9/19] w-72 mx-auto bg-white rounded-3xl shadow-xl overflow-hidden">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b z-10">
                  <div className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <h1 className="text-lg font-semibold">Mercado Central</h1>
                          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                        </div>
                        <p className="text-sm text-gray-500">Catálogo digital</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Search Bar */}
                <div className="bg-white border-b px-4 py-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Buscar productos..."
                      className="w-full pl-10 pr-4 py-2 bg-gray-50 rounded-xl text-gray-900 placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-[#3B7C5B]"
                    />
                  </div>
                </div>

                {/* Products */}
                <div className="p-4 space-y-3 overflow-auto max-h-[500px]">
                  {[
                    { 
                      name: 'Tomates Perita',
                      price: '$950',
                      unit: 'cajón',
                      stock: 15,
                      category: 'Verduras',
                      image: 'bg-red-100',
                      featured: true
                    },
                    { 
                      name: 'Papas Blancas',
                      price: '$750',
                      unit: 'bolsa',
                      stock: 8,
                      category: 'Verduras',
                      image: 'bg-yellow-100'
                    },
                    { 
                      name: 'Cebollas',
                      price: '$600',
                      unit: 'bolsa',
                      stock: 20,
                      category: 'Verduras',
                      image: 'bg-purple-100'
                    },
                    { 
                      name: 'Manzanas Red',
                      price: '$1200',
                      unit: 'cajón',
                      stock: 12,
                      category: 'Frutas',
                      image: 'bg-red-50'
                    },
                    { 
                      name: 'Naranjas',
                      price: '$850',
                      unit: 'bolsa',
                      stock: 25,
                      category: 'Frutas',
                      image: 'bg-orange-100',
                      featured: true
                    }
                  ].map((item, i) => (
                    <div key={i} className="bg-white rounded-xl p-4 shadow-sm border">
                      <div className="flex gap-4">
                        <div className={`w-16 h-16 ${item.image} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <Package className="w-8 h-8 text-gray-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="text-xs text-[#3B7C5B] font-medium">{item.category}</div>
                            {item.featured && (
                              <Star className="w-4 h-4 text-[#3B7C5B] fill-current" />
                            )}
                          </div>
                          <h3 className="font-medium text-lg">{item.name}</h3>
                          <div className="text-sm text-gray-500 space-x-2">
                            <span className="text-[#3B7C5B] font-bold">{item.price}/{item.unit}</span>
                            <span>•</span>
                            <span>Stock: {item.stock}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Action Bar */}
                <div className="sticky bottom-0 bg-white border-t p-4">
                  <button 
                    onClick={onShowCatalog}
                    className="w-full bg-[#25D366] text-white py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-2"
                  >
                    <MessageSquare className="w-4 h-4" />
                    Ver catálogo completo
                  </button>
                </div>
              </div>

              {/* Floating Update Badge */}
              <div className="absolute -top-4 right-4 bg-white px-4 py-2 rounded-full shadow-lg border border-gray-100 flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-sm font-medium">En vivo</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Before/After Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold mb-6">Tu negocio antes y después de Tibó</h2>
            <p className="text-xl text-gray-600">
              Descubrí cómo Tibó transforma la gestión de tu negocio mayorista
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Sin Tibó */}
            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-full bg-red-100 mx-auto mb-4 flex items-center justify-center">
                  <XCircle className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-2xl font-bold mb-2">Sin Tibó</h3>
                <p className="text-gray-600">Gestión manual y desorganizada</p>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-medium">Inventario desactualizado</p>
                    <p className="text-sm text-gray-600">Errores constantes en stock y precios</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-medium">Ventas lentas</p>
                    <p className="text-sm text-gray-600">5-10 minutos por venta con errores frecuentes</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-medium">Catálogo desorganizado</p>
                    <p className="text-sm text-gray-600">Lista de precios en Excel o papel</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-medium">Tickets manuales</p>
                    <p className="text-sm text-gray-600">Errores de cálculo y mala presentación</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-medium">Sin control de equipo</p>
                    <p className="text-sm text-gray-600">Confusión y falta de coordinación</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-medium">Pérdida de ventas</p>
                    <p className="text-sm text-gray-600">Clientes insatisfechos y ventas perdidas</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-4 bg-red-50 rounded-xl">
                <p className="text-sm text-red-600 text-center">
                  <strong>Resultado:</strong> Pérdida de tiempo, dinero y clientes
                </p>
              </div>
            </div>

            {/* Con Tibó */}
            <div className="bg-[#3B7C5B]/5 rounded-2xl p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-full bg-[#3B7C5B]/20 mx-auto mb-4 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-[#3B7C5B]" />
                </div>
                <h3 className="text-2xl font-bold mb-2">Con Tibó</h3>
                <p className="text-[#3B7C5B]">Gestión profesional automatizada</p>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#3B7C5B] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-medium">Inventario en tiempo real</p>
                    <p className="text-sm text-gray-600">Stock y precios siempre actualizados</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#3B7C5B] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-medium">Ventas express</p>
                    <p className="text-sm text-gray-600">30 segundos por venta sin errores</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#3B7C5B] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-medium">Catálogo digital profesional</p>
                    <p className="text-sm text-gray-600">Actualización automática en WhatsApp</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#3B7C5B] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-medium">Tickets profesionales</p>
                    <p className="text-sm text-gray-600">Impresión automática con tu marca</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#3B7C5B] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-medium">Equipo coordinado</p>
                    <p className="text-sm text-gray-600">Roles y permisos específicos</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#3B7C5B] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-medium">Más ventas</p>
                    <p className="text-sm text-gray-600">Clientes satisfechos que vuelven</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-4 bg-[#3B7C5B]/10 rounded-xl">
                <p className="text-sm text-[#3B7C5B] text-center">
                  <strong>Resultado:</strong> Ahorro de tiempo y aumento de ventas
                </p>
              </div>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="mt-16 text-center">
            <button className="bg-[#3B7C5B] text-white px-8 py-4 rounded-xl text-lg font-medium hover:bg-[#2E6346] transition-colors inline-flex items-center gap-2 shadow-lg shadow-[#3B7C5B]/25 hover:shadow-xl hover:shadow-[#3B7C5B]/30">
              Transformá tu negocio ahora
              <ArrowRight className="w-5 h-5" />
            </button>
            <p className="mt-4 text-gray-500">14 días gratis • Sin tarjeta de crédito</p>
          </div>
        </div>
      </section>

      {/* WhatsApp Catalog Sharing Section */}
      <section className="py-24 bg-gradient-to-br from-[#25D366]/10 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#25D366] text-white mb-8">
              <MessageSquare className="w-5 h-5" />
              <span className="text-sm font-medium">Integración con WhatsApp</span>
            </div>
            <h2 className="text-4xl font-bold mb-6">Tu catálogo siempre a mano en WhatsApp</h2>
            <p className="text-xl text-gray-600">
              Compartí tu catálogo digital actualizado con todos tus clientes en un click. 
              Ellos ven tus productos y te contactan directamente.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Card 1 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="w-16 h-16 rounded-full bg-[#25D366]/10 flex items-center justify-center mb-6">
                <Share2 className="w-8 h-8 text-[#25D366]" />
              </div>
              <h3 className="text-xl font-bold mb-4">Compartí fácil</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#25D366] flex-shrink-0 mt-1" />
                  <span className="text-gray-600">Link público personalizado para tu negocio</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#25D366] flex-shrink-0 mt-1" />
                  <span className="text-gray-600">Código QR para mostrar en tu local</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#25D366] flex-shrink-0 mt-1" />
                  <span className="text-gray-600">Botón directo a WhatsApp</span>
                </li>
              </ul>
            </div>

            {/* Card 2 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="w-16 h-16 rounded-full bg-[#25D366]/10 flex items-center justify-center mb-6">
                <Clock className="w-8 h-8 text-[#25D366]" />
              </div>
              <h3 className="text-xl font-bold mb-4">Siempre actualizado</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#25D366] flex-shrink-0 mt-1" />
                  <span className="text-gray-600">Precios y stock en tiempo real</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#25D366] flex-shrink-0 mt-1" />
                  <span className="text-gray-600">Fotos y descripciones detalladas</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#25D366] flex-shrink-0 mt-1" />
                  <span className="text-gray-600">Categorías organizadas</span>
                </li>
              </ul>
            </div>

            {/* Card 3 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="w-16 h-16 rounded-full bg-[#25D366]/10 flex items-center justify-center mb-6">
                <MessageSquare className="w-8 h-8 text-[#25D366]" />
              </div>
              <h3 className="text-xl font-bold mb-4">Pedidos simples</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#25D366] flex-shrink-0 mt-1" />
                  <span className="text-gray-600">Chat directo desde el catálogo</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#25D366] flex-shrink-0 mt-1" />
                  <span className="text-gray-600">Mensajes predefinidos de pedidos</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#25D366] flex-shrink-0 mt-1" />
                  <span className="text-gray-600">Historial de conversaciones</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Demo Preview */}
          <div className="mt-16 max-w-xs mx-auto">
            <div className="bg-[#DCF8C6] rounded-t-xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#25D366] flex items-center justify-center">
                  <Package className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1">
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <p className="text-sm font-medium mb-2">🛍️ Catálogo Mayorista</p>
                    <p className="text-sm text-gray-600 mb-2">¡Mirá nuestros productos actualizados!</p>
                    <div className="bg-[#25D366] text-white text-sm py-1 px-3 rounded inline-flex items-center gap-1">
                      <Share2 className="w-4 h-4" />
                      Ver catálogo
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Ahora</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-b-xl p-4 shadow-lg">
              <button className="w-full bg-[#25D366] text-white py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Compartir catálogo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50" id="pricing">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold mb-6">Elegí el plan perfecto para tu negocio</h2>
            <p className="text-xl text-gray-600">
              Desde emprendedores hasta grandes mayoristas, tenemos el plan ideal para vos
            </p>
          </div>
          
          <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-8">
            {[
              {
                name: "Hobby",
                price: "Gratis",
                yearlyPrice: "para siempre",
                features: [
                  "Catálogo digital",
                  "Link público",
                  "Hasta 50 productos",
                  "1 usuario",
                  "Soporte por email"
                ],
                cta: "Comenzar gratis",
                highlighted: false,
                badge: "GRATIS"
              },
              {
                name: "Base",
                price: "$40",
                yearlyPrice: "$400/año",
                features: [
                  "Todo lo de Hobby más:",
                  "Hasta 200 productos",
                  "Registro de ventas básico",
                  "Impresión de tickets",
                  "Catálogo por WhatsApp",
                  "Soporte prioritario"
                ],
                cta: "Comenzar prueba gratis",
                highlighted: false,
                badge: "POPULAR"
              },
              {
                name: "Plus",
                price: "$100",
                yearlyPrice: "$1,000/año",
                features: [
                  "Todo lo de Base más:",
                  "Hasta 5 usuarios",
                  "Productos ilimitados",
                  "Control de lotes",
                  "Reportes avanzados",
                  "Roles y permisos",
                  "Personalización de tickets",
                  "Backup automático"
                ],
                cta: "Comenzar prueba gratis",
                highlighted: true,
                badge: "RECOMENDADO"
              },
              {
                name: "Pro",
                price: "$200",
                yearlyPrice: "$2,000/año",
                features: [
                  "Todo lo de Plus más:",
                  "Usuarios ilimitados",
                  "API para integraciones",
                  "Múltiples sucursales",
                  "Soporte 24/7",
                  "Onboarding personalizado",
                  "Account manager",
                  "Hardware incluido"
                ],
                cta: "Contactar ventas",
                highlighted: false,
                badge: "ENTERPRISE"
              }
            ].map((plan, i) => (
              <div 
                key={i} 
                className={`
                  relative rounded-2xl transition-all duration-300 hover:scale-105
                  ${plan.highlighted ? 
                    'bg-gradient-to-br from-[#3B7C5B] to-[#2E6346] text-white shadow-xl shadow-[#3B7C5B]/25' : 
                    'bg-white text-gray-900 border border-gray-100 hover:border-[#3B7C5B]/20 hover:shadow-lg'
                  }
                `}
              >
                {/* Badge */}
                <div className={`
                  absolute -top-4 left-1/2 transform -translate-x-1/2
                  px-4 py-1 rounded-full text-xs font-bold
                  ${plan.highlighted ? 
                    'bg-white text-[#3B7C5B]' : 
                    'bg-[#3B7C5B]/10 text-[#3B7C5B]'
                  }
                `}>
                  {plan.badge}
                </div>

                <div className="p-8">
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                    <div className="space-y-1">
                      <div className="text-4xl font-bold">{plan.price}</div>
                      <div className={`text-sm ${plan.highlighted ? 'text-white/80' : 'text-gray-500'}`}>
                        {plan.yearlyPrice}
                      </div>
                    </div>
                  </div>

                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, j) => (
                      <li key={j} className="flex items-start gap-3">
                        <CheckCircle2 className={`w-5 h-5 flex-shrink-0 ${
                          plan.highlighted ? 'text-white' : 'text-[#3B7C5B]'
                        }`} />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button 
                    className={`
                      w-full py-3 rounded-xl font-medium transition-colors
                      ${plan.highlighted ?
                        'bg-white text-[#3B7C5B] hover:bg-gray-50' :
                        'bg-[#3B7C5B] text-white hover:bg-[#2E6346]'
                      }
                    `}
                  >
                    {plan.cta}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Additional Info */}
          <div className="mt-16 text-center">
            <p className="text-gray-500">
              ¿Necesitás un plan personalizado? {' '}
              <a href="#contact" className="text-[#3B7C5B] font-medium hover:underline">
                Contactanos
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16">Preguntas frecuentes</h2>
          
          <div className="max-w-3xl mx-auto space-y-6">
            {[
              {
                question: "¿Necesito comprar algún equipo especial?",
                answer: "No, Tibó funciona en cualquier dispositivo con acceso a internet. Para la impresión de tickets, es compatible con la mayoría de impresoras térmicas del mercado."
              },
              {
                question: "¿Puedo probar Tibó antes de pagar?",
                answer: "Sí, ofrecemos una prueba gratuita de 14 días con todas las funcionalidades del Plan Equipo. No requiere tarjeta de crédito."
              },
              {
                question: "¿Cómo funciona la integración con WhatsApp?",
                answer: "Tibó genera automáticamente un catálogo digital con tus productos y precios actualizados, que puedes compartir directamente por WhatsApp con tus clientes."
              },
              {
                question: "¿Puedo cambiar de plan en cualquier momento?",
                answer: "Sí, puedes actualizar o cambiar tu plan en cualquier momento. Los cambios se reflejarán en tu próxima factura."
              },
              {
                question: "¿Qué soporte técnico ofrecen?",
                answer: "Todos los planes incluyen soporte técnico. Los planes Equipo y Premium incluyen soporte prioritario y el Plan Premium cuenta con soporte 24/7."
              }
            ].map((faq, i) => (
              <div key={i} className="bg-white rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <HelpCircle className="w-6 h-6 text-green-700" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">{faq.question}</h3>
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="text-2xl font-bold text-white mb-4">Tibó</div>
              <p className="text-sm">Software de gestión para mayoristas</p>
            </div>
            
            <div>
              <h4 className="text-white font-medium mb-4">Producto</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Características</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Precios</a></li>
                <li><a href="#how-it-works" className="hover:text-white transition-colors">Cómo funciona</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-medium mb-4">Soporte</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Centro de ayuda</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contacto</a></li>
                <li><a href="#" className="hover:text-white transition-colors">FAQ</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-medium mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Términos de servicio</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacidad</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8 text-sm text-center">
            © {new Date().getFullYear()} Tibó. Todos los derechos reservados.
          </div>
        </div>
      </footer>
    </div>
  );
} 